import { useCustomers } from "@/api/customer";
import { useAuthStore } from "@/lib/auth";
import { useHeliosWebCallingFeature } from "@/lib/feature-flags/use-feature-flag";
import { formatPhoneNumber, getCustomerName } from "@/lib/utils";
import type { RemoteParty } from "./voice-provider";

/**
 * Whether web calling is available for the active gym. Gated by the
 * `helios-web-calling` PostHog flag plus the gym having a phone number
 * (calls are placed from the gym's Twilio number).
 *
 * Phase 2 will additionally require the number's Twilio voice capability
 * once the API exposes it; call sites only check this helper.
 */
export function useVoiceCallingEnabled(): boolean {
  const { enabled } = useHeliosWebCallingFeature();
  const gym = useAuthStore((state) => state.gym);
  return enabled && Boolean(gym?.phoneNumber);
}

/**
 * Resolves display info for the other party on a call. Prefers the name
 * captured when the call started (outbound from a contact); otherwise
 * looks the caller up by phone number in the active gym's contacts.
 */
export function useRemoteDisplay(remote: RemoteParty | null): {
  name: string | null;
  formattedPhoneNumber: string | null;
} {
  const gym = useAuthStore((state) => state.gym);
  const phoneNumber = remote?.phoneNumber?.trim() || undefined;
  const shouldLookup = Boolean(phoneNumber && gym?.id && !remote?.name);

  const { data: customers } = useCustomers(
    { gym: gym?.id ?? "", phoneNumber, limit: 1 },
    { skip: !shouldLookup },
  );

  const customer = shouldLookup ? customers?.[0] : undefined;
  const name = remote?.name ?? (customer ? getCustomerName(customer) : null);

  return {
    name,
    formattedPhoneNumber: phoneNumber ? formatPhoneNumber(phoneNumber) : null,
  };
}
